// Generated at deploy time by scripts/snapshotConfig.js — the landing page's
// share of /config (allowlisted fields only, images as static files), or {}
// when the build has no snapshot.
import CONFIG_SNAPSHOT from '../generated/configSnapshot.json';
import { DEFAULT_THEME_COLORS } from '../constants/bookingConst';
import { isValidHex } from './colorUtil';

/**
 * The build-time config snapshot, if it belongs to THIS client's project.
 *
 * The projectId check matters because the snapshot file is gitignored: it
 * survives a branch switch, so a local build of one client could otherwise
 * pick up another client's name, rates, colours and logo.
 *
 * @returns {object|null} the snapshot's (partial) config, or null.
 */
export const readBuildSnapshot = () => {
  if (!CONFIG_SNAPSHOT?.config) return null;
  if (CONFIG_SNAPSHOT.projectId !== process.env.REACT_APP_FIREBASE_PROJECT_ID) return null;
  return CONFIG_SNAPSHOT.config;
};

/**
 * The first valid "#abc"/"#aabbcc" value in the list, trimmed.
 *
 * @param {...unknown} candidates - in priority order; the last should always be valid.
 * @returns {string|undefined}
 */
const pickHex = (...candidates) => candidates.find(isValidHex)?.trim();

/**
 * The club's branding as it ships in this build: logo, name and theme colours.
 *
 * Reads ONLY build-time sources — the config snapshot, then the client's own
 * .env and public/logo.png — never the live config, the IndexedDB cache or
 * localStorage. That is what lets the welcome splash paint the club's
 * look on the very first frame, before App Check, sign-in or any database
 * read has happened.
 *
 * Logo: the snapshot's logo is a resized WebP (~256px) exported next to the
 * build, so it is preferred over public/logo.png, which is the full-size
 * favicon upload.
 *
 * @returns {{
 *   clubName: string,
 *   logoUrl: string,
 *   colors: {
 *     background: string, headerBackground: string, active: string, brand: string,
 *   },
 * }}
 */
export const getBuildBranding = () => {
  const snapshot = readBuildSnapshot() || {};
  const clubName = typeof snapshot.courtName === 'string' ? snapshot.courtName.trim() : '';

  return {
    clubName,
    logoUrl: snapshot.logoUrl || `${process.env.PUBLIC_URL}/logo.png`,
    colors: {
      background: pickHex(snapshot.themeBackgroundColor, DEFAULT_THEME_COLORS.background),
      headerBackground: pickHex(snapshot.themeHeaderBackgroundColor, DEFAULT_THEME_COLORS.headerBackground),
      active: pickHex(snapshot.themeActiveColor, DEFAULT_THEME_COLORS.active),
      brand: pickHex(snapshot.themeBrandColor, DEFAULT_THEME_COLORS.brand),
    },
  };
};

/**
 * Every image file this build's snapshot exported (public/snapshot/*): the
 * logo, landing and gallery photos, sport cards and each sport's gallery —
 * including the photos the time-slot screen shows. Empty when the build has
 * no snapshot for this client.
 *
 * @returns {string[]} unique static image URLs, e.g. '/snapshot/hero-<hash>.webp'.
 */
export const getSnapshotImageUrls = () => {
  if (!readBuildSnapshot()) return [];
  const urls = Object.values(CONFIG_SNAPSHOT.images || {})
    .map(entry => entry?.url)
    .filter(url => typeof url === 'string' && url);
  return [...new Set(urls)];
};
